import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Grid,
  LinearProgress,
} from '@mui/material';
import {
  Storage as StorageIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon,
  Videocam as VideoIcon,
  Sync as SyncIcon,
  InsertDriveFile as FileIcon,
  Compress as CompressIcon,
} from '@mui/icons-material';
import SettingsRowShell from './SettingsRowShell';
import storageService from '../../services/storageService';

/* ── helpers ──────────────────────────────────────────────────────── */

const formatBytes = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(i > 1 ? 2 : 0)} ${sizes[i]}`;
};

const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);

/* ── stat card ────────────────────────────────────────────────────── */

const StatCard = ({ icon, label, value, sub, color = 'primary.main' }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      minWidth: 0,
    }}
  >
    <Box sx={{ color, display: 'flex' }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="h6" fontWeight={700} noWrap>{value}</Typography>
      <Typography variant="caption" color="text.secondary" noWrap>{label}</Typography>
      {sub && (
        <Typography variant="caption" display="block" color="text.disabled" noWrap>{sub}</Typography>
      )}
    </Box>
  </Paper>
);

/* ── main component ───────────────────────────────────────────────── */

const StorageRow = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillMsg, setBackfillMsg] = useState(null);

  const fetchMetrics = useCallback(async () => {
    try {
      setLoading(true);
      const data = await storageService.getMetrics();
      setMetrics(data);
    } catch (err) {
      console.error('Failed to load storage metrics:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMetrics(); }, [fetchMetrics]);

  const handleBackfill = async () => {
    try {
      setBackfilling(true);
      setBackfillMsg(null);
      const res = await storageService.triggerBackfill();
      if (res.status === 'nothing_to_process') {
        setBackfillMsg({ text: 'All existing files are already tracked.', severity: 'info' });
      } else {
        setBackfillMsg({ text: `Backfill started — processing ${res.urls_to_process} file(s). Refresh in a moment.`, severity: 'success' });
        // Re-fetch after a short delay to pick up progress
        setTimeout(fetchMetrics, 5000);
      }
    } catch (err) {
      setBackfillMsg({ text: 'Backfill failed: ' + (err.response?.data?.detail || err.message), severity: 'error' });
    } finally {
      setBackfilling(false);
    }
  };

  const savingsPct = metrics ? pct(metrics.compression_savings_bytes, metrics.total_original_size_bytes) : 0;
  const statusLabel = metrics ? formatBytes(metrics.total_size_bytes) : '…';

  return (
    <SettingsRowShell
      icon={<StorageIcon />}
      title="Storage & Media"
      description="Track media file usage across issues"
      status={{ label: statusLabel, color: metrics && metrics.total_file_count > 0 ? 'info' : 'default' }}
      actions={
        <Tooltip title="Refresh metrics">
          <IconButton onClick={fetchMetrics} size="small" disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      {loading && !metrics ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : !metrics ? (
        <Alert severity="warning">Unable to load storage metrics.</Alert>
      ) : metrics.total_file_count === 0 ? (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No media files tracked yet. Upload files via the Issues dialog, or sync existing files.
          </Typography>
          <Button
            variant="outlined"
            startIcon={backfilling ? <CircularProgress size={16} /> : <SyncIcon />}
            onClick={handleBackfill}
            disabled={backfilling}
          >
            Sync Existing Files
          </Button>
          {backfillMsg && <Alert severity={backfillMsg.severity} sx={{ mt: 2 }}>{backfillMsg.text}</Alert>}
        </Box>
      ) : (
        <>
          {/* ── Summary cards ── */}
          <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
            <Grid item xs={6} sm={3}>
              <StatCard
                icon={<StorageIcon />}
                label="Total Storage"
                value={formatBytes(metrics.total_size_bytes)}
                sub={`${metrics.total_file_count} file${metrics.total_file_count !== 1 ? 's' : ''}`}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                icon={<ImageIcon />}
                label="Images"
                value={formatBytes(metrics.image_size_bytes)}
                sub={`${metrics.image_count} file${metrics.image_count !== 1 ? 's' : ''}`}
                color="success.main"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                icon={<VideoIcon />}
                label="Videos"
                value={formatBytes(metrics.video_size_bytes)}
                sub={`${metrics.video_count} file${metrics.video_count !== 1 ? 's' : ''}`}
                color="warning.main"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <StatCard
                icon={<CompressIcon />}
                label="Compression Saved"
                value={formatBytes(metrics.compression_savings_bytes)}
                sub={metrics.total_original_size_bytes > 0 ? `${savingsPct}% reduction` : '—'}
                color="info.main"
              />
            </Grid>
          </Grid>

          {/* ── Per-user breakdown ── */}
          {metrics.by_user.length > 0 && (
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Uploads by User
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell align="right">Files</TableCell>
                      <TableCell align="right">Size</TableCell>
                      <TableCell align="right">Share</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metrics.by_user.map((u) => (
                      <TableRow key={u.user_id}>
                        <TableCell>{u.username}</TableCell>
                        <TableCell align="right">{u.file_count}</TableCell>
                        <TableCell align="right">{formatBytes(u.total_size_bytes)}</TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={pct(u.total_size_bytes, metrics.total_size_bytes)}
                              sx={{ width: 60, height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="caption">{pct(u.total_size_bytes, metrics.total_size_bytes)}%</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* ── Top issues by storage ── */}
          {metrics.top_issues.length > 0 && (
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Top Issues by Storage
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Issue</TableCell>
                      <TableCell align="right">Files</TableCell>
                      <TableCell align="right">Size</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metrics.top_issues.map((iss) => (
                      <TableRow key={iss.issue_id}>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
                            <Chip label={`#${iss.issue_id}`} size="small" variant="outlined" sx={{ mr: 1, fontWeight: 600 }} />
                            {iss.title}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{iss.file_count}</TableCell>
                        <TableCell align="right">{formatBytes(iss.total_size_bytes)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* ── Recent uploads ── */}
          {metrics.recent_uploads.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Recent Uploads
              </Typography>
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>File</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Size</TableCell>
                      <TableCell>Uploaded By</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metrics.recent_uploads.map((f) => (
                      <TableRow key={f.id}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <FileIcon fontSize="small" color="action" />
                            <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>{f.filename}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={f.mime_type?.startsWith('video/') ? 'Video' : f.mime_type?.startsWith('image/') ? 'Image' : 'Other'}
                            size="small"
                            color={f.mime_type?.startsWith('video/') ? 'warning' : 'success'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">{formatBytes(f.file_size_bytes)}</TableCell>
                        <TableCell>{f.uploader_name || '—'}</TableCell>
                        <TableCell>{new Date(f.created_at).toLocaleDateString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* ── Backfill button ── */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={backfilling ? <CircularProgress size={14} /> : <SyncIcon />}
              onClick={handleBackfill}
              disabled={backfilling}
            >
              Sync Existing Files
            </Button>
            <Typography variant="caption" color="text.secondary">
              Scans issues for media files not yet tracked and fetches their sizes from storage.
            </Typography>
          </Box>
          {backfillMsg && <Alert severity={backfillMsg.severity} sx={{ mt: 1.5 }}>{backfillMsg.text}</Alert>}
        </>
      )}
    </SettingsRowShell>
  );
};

export default StorageRow;
